import type { UseQueryOptions } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import type { DataImport, DataImportPayload } from "./dataImport.types";
import { api } from "./apiClient";

const BASE_ENDPOINT_DATA_IMPORT = `${FORMS_API_BASE_ENDPOINT}/import`;

const dataImportKeys = {
  all: () => ["data-imports"] as const,
};

export const useGetDataImports = (options: Pick<UseQueryOptions, "refetchInterval"> = { refetchInterval: false }) => {
  return useQuery({
    queryKey: dataImportKeys.all(),
    queryFn: () => api.get(`${BASE_ENDPOINT_DATA_IMPORT}`) as Promise<DataImport | unknown>,
    throwOnError: true,
    refetchInterval: options.refetchInterval,
  });
};

const downloadFile = async (path: string, fallbackFilename: string) => {
  const response = await (api.get(`${BASE_ENDPOINT_DATA_IMPORT}/${path}`) as Promise<Response>);
  return { response, filename: getFilename(response, fallbackFilename) };
};

export const useGetDataImportTemplate = () =>
  useMutation({ mutationFn: () => downloadFile("template", "data-import-template.xlsx") });

export const useGetDataImportValidatedFile = () =>
  useMutation({ mutationFn: () => downloadFile("validationfile", "data-import.xlsx") });

export const useCreateDataImport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DataImportPayload) => {
      const formData = new FormData();
      formData.append("File", payload.file);
      return api.postFile(BASE_ENDPOINT_DATA_IMPORT, formData) as Promise<DataImport>;
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

const getFilename = (response: Response, fallback: string) => {
  const contentDisposition = response.headers.get("content-disposition");
  return contentDisposition ? contentDisposition.split("filename=")[1].split(";")[0] : fallback;
};
